/**
 * CorporateLCADashboard
 *
 * Tenant-wide LCA rollup. Read-only in this iteration:
 * - Site/project counts in the period.
 * - Inventory flows summed across sites (LCA quantitative is summed per
 *   stakeholder decision).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Recycle, RefreshCw, Plus, X, ChevronRight, Database } from 'lucide-react';
import CorporatePeriodSelector from './CorporatePeriodSelector';
import LCAProjectView from '../../../components/lca/LCAProjectView';
import DatasetLibrary from '../../../components/lca/DatasetLibrary';
import { useSite } from '../../../context/SiteContext';
import { usePermissions } from '../../../context/PermissionContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CorporateLCADashboard = () => {
  const [periodId, setPeriodId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [projects, setProjects] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', site_id: '', master_product_id: '' });
  const [products, setProducts] = useState([]);
  const [showInlineProduct, setShowInlineProduct] = useState(false);
  const [newProductForm, setNewProductForm] = useState({ name: '', code: '', unit: 'MT' });
  // When set, render the 4-phase LCA project workspace instead of the
  // corporate rollup. LCAProjectView is the same component used in the
  // study-scoped flow — it self-fetches by project_id and synthesizes a
  // {site_id, site_name} object for its children so corporate-direct (no
  // site) projects work the same way.
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  // When true, render the tenant-wide DatasetLibrary in corporate mode so
  // users can curate factors before creating an LCA project.
  const [browsingDatasets, setBrowsingDatasets] = useState(false);

  const { availableSites } = useSite();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('lca', 'write_corporate');

  // Load product master whenever the create-project dialog opens.
  useEffect(() => {
    if (!showNewProject) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/api/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setProducts(r.data?.products || []))
      .catch((err) => console.error('Failed to load product master', err));
  }, [showNewProject]);

  const createMasterProduct = async () => {
    if (!newProductForm.name.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/api/products`,
        {
          name: newProductForm.name,
          code: newProductForm.code || null,
          unit: newProductForm.unit || 'MT',
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const created = r.data?.product;
      if (created) {
        setProducts((p) => [...p, created]);
        setForm((f) => ({ ...f, master_product_id: created.id, name: f.name || created.name }));
      }
      setShowInlineProduct(false);
      setNewProductForm({ name: '', code: '', unit: 'MT' });
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Failed to create product');
    } finally {
      setCreating(false);
    }
  };

  const fetchAll = useCallback(async () => {
    if (!periodId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, projRes, invRes] = await Promise.all([
        axios.get(`${API}/api/lca/corporate/dashboard?period_id=${periodId}`, { headers }),
        axios.get(`${API}/api/lca/corporate/projects?period_id=${periodId}`, { headers }),
        axios.get(`${API}/api/lca/corporate/inventory?period_id=${periodId}`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setProjects(projRes.data);
      setInventory(invRes.data);
    } catch (err) {
      console.error('Corporate LCA fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const submitNewProject = async (e) => {
    e.preventDefault();
    if (!periodId) return;
    setCreating(true);
    setCreateError(null);
    try {
      const token = localStorage.getItem('token');
      const siteOption = availableSites.find((s) => s.site_id === form.site_id);
      const payload = {
        name: form.name,
        description: form.description || null,
        site_id: form.site_id || null,
        site_name: siteOption?.site_name || null,
        master_product_id: form.master_product_id || null,
      };
      const res = await axios.post(
        `${API}/api/lca/corporate/projects?period_id=${periodId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowNewProject(false);
      setForm({ name: '', description: '', site_id: '', master_product_id: '' });
      // Drop straight into the 4-phase workspace so the user can start
      // Goal & Scope without an extra click.
      if (res.data?.id) {
        setSelectedProjectId(res.data.id);
      } else {
        fetchAll();
      }
    } catch (err) {
      console.error('Create corporate LCA project failed', err);
      setCreateError(err.response?.data?.detail || err.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  // 4-phase workspace — same component the study-scoped LCAModule uses.
  if (selectedProjectId) {
    return (
      <LCAProjectView
        projectId={selectedProjectId}
        onBack={() => {
          setSelectedProjectId(null);
          fetchAll();
        }}
      />
    );
  }

  // Tenant-wide dataset library, optionally filtered by product (master).
  if (browsingDatasets) {
    return (
      <DatasetLibrary
        corporateMode
        onBack={() => setBrowsingDatasets(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Recycle className="w-6 h-6 text-teal-600" />
          <h2 className="text-2xl font-bold text-gray-900">Corporate LCA Rollup</h2>
        </div>
        <div className="flex items-center gap-3">
          <CorporatePeriodSelector value={periodId} onChange={setPeriodId} />
          <button
            onClick={() => setBrowsingDatasets(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-teal-300 text-teal-700 text-sm font-medium hover:bg-teal-50"
            title="Browse the tenant-wide dataset library before creating a project"
          >
            <Database className="w-4 h-4" /> Datasets
          </button>
          {canWrite && (
            <button
              onClick={() => setShowNewProject(true)}
              disabled={!periodId}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-40"
              title={periodId ? 'Create a new LCA project at corporate level' : 'Pick a period first'}
            >
              <Plus className="w-4 h-4" /> New Project
            </button>
          )}
        </div>
      </div>

      {showNewProject && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">New Corporate LCA Project</h3>
              <button onClick={() => setShowNewProject(false)} className="text-gray-500 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitNewProject} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product <span className="text-gray-400 font-normal">(from product master)</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={form.master_product_id}
                    onChange={(e) => {
                      const id = e.target.value;
                      const prod = products.find((p) => p.id === id);
                      setForm((f) => ({
                        ...f,
                        master_product_id: id,
                        name: prod && !f.name ? `${prod.name} — LCA` : f.name,
                      }));
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">— Select a product —</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.code ? ` (${p.code})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowInlineProduct((v) => !v)}
                    className="px-3 py-2 rounded-lg border border-teal-300 text-teal-700 text-sm hover:bg-teal-50"
                  >
                    + New
                  </button>
                </div>
                {showInlineProduct && (
                  <div className="mt-2 p-3 rounded-lg bg-gray-50 border border-gray-200 space-y-2">
                    <div className="text-xs font-medium text-gray-600">New product master entry</div>
                    <input
                      type="text"
                      placeholder="Product name"
                      value={newProductForm.name}
                      onChange={(e) => setNewProductForm({ ...newProductForm, name: e.target.value })}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Code / SKU (optional)"
                        value={newProductForm.code}
                        onChange={(e) => setNewProductForm({ ...newProductForm, code: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Unit"
                        value={newProductForm.unit}
                        onChange={(e) => setNewProductForm({ ...newProductForm, unit: e.target.value })}
                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={createMasterProduct}
                      disabled={!newProductForm.name.trim() || creating}
                      className="w-full px-2 py-1 rounded bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-40"
                    >
                      {creating ? 'Saving…' : 'Save product'}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Distillery Ethanol — LCA 2026"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Functional unit, scope, allocation method…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site <span className="text-gray-400 font-normal">(optional — leave blank for site-agnostic)</span>
                </label>
                <select
                  value={form.site_id}
                  onChange={(e) => setForm({ ...form, site_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">— No site —</option>
                  {availableSites.map((s) => (
                    <option key={s.site_id} value={s.site_id}>
                      {s.site_name || s.site_id}
                    </option>
                  ))}
                </select>
              </div>
              {createError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {createError}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewProject(false)}
                  className="px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !form.name.trim()}
                  className="px-4 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-40"
                >
                  {creating ? 'Creating…' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Sites" value={dashboard.site_count ?? 0} />
          <StatCard label="LCA Projects" value={dashboard.project_count ?? 0} />
          <StatCard label="Period" value={dashboard.period?.name || '—'} />
        </div>
      )}

      {!loading && projects && projects.sites?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Projects by Site</h3>
          <div className="space-y-4">
            {projects.sites.map((site) => (
              <div key={site.site_id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-gray-900">{site.site_name || site.site_id}</div>
                  <span className="text-xs text-gray-500">{site.project_count} project(s)</span>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  {site.projects.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => setSelectedProjectId(p.id)}
                        className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-teal-50 group"
                      >
                        <span className="text-left font-medium text-gray-900 group-hover:text-teal-700">
                          {p.name}
                        </span>
                        <span className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="px-2 py-0.5 rounded bg-gray-100">Phase {p.current_phase}</span>
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-teal-600" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && inventory && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Inventory Flows (summed across sites)
          </h3>
          {(!inventory.flows || inventory.flows.length === 0) ? (
            <p className="text-sm text-gray-500">No inventory data found for this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2">Flow</th>
                    <th className="text-left px-3 py-2">Category</th>
                    <th className="text-left px-3 py-2">Unit</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">Sites</th>
                  </tr>
                </thead>
                <tbody>
                  {inventory.flows.map((f, idx) => (
                    <tr key={`${f.flow_name}-${f.unit}-${idx}`} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">{f.flow_name}</td>
                      <td className="px-3 py-2 text-gray-600">{f.category || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{f.unit}</td>
                      <td className="px-3 py-2 text-right text-gray-900 font-semibold">
                        {Number(f.total_quantity || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500">
                        {f.contributing_site_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
  </div>
);

export default CorporateLCADashboard;
